import type { CombatFx } from '../model/presentation'
import { playSynth } from './synth'
import { loadAudioPreferences, saveAudioPreferences } from './preferences'
import {
  clampVolume,
  SOUND_POLICY,
  type AudioPreferencesV1,
  type SoundId
} from './types'
import { admitSound, globalVoiceCap, shouldStealFor } from './voiceBudget'

interface ActiveVoice {
  id: SoundId
  stop: () => void
  priority: number
}

/**
 * Singleton-ish audio facade. Presentation only — never mutates GameSession.
 * WebAudio resumes after a legitimate user gesture via unlock().
 */
export class AudioManager {
  private prefs: AudioPreferencesV1
  private ctx: AudioContext | null = null
  private masterGain: GainNode | null = null
  private sfxGain: GainNode | null = null
  private uiGain: GainNode | null = null
  private unlocked = false
  private lastPlayedAt = new Map<SoundId, number>()
  private voices: ActiveVoice[] = []
  private simulationSpeed = 1
  private lastMatchState: string | null = null
  private seenBossIds = new Set<string>()
  private listeners = new Set<() => void>()
  private visibilityHandler: (() => void) | null = null

  constructor(prefs?: AudioPreferencesV1) {
    this.prefs = prefs ?? loadAudioPreferences()
    if (typeof document !== 'undefined') {
      this.visibilityHandler = () => {
        if (document.hidden) {
          // Drop bookkeeping backlog — do not replay on return.
          this.lastPlayedAt.clear()
        }
      }
      document.addEventListener('visibilitychange', this.visibilityHandler)
    }
  }

  get preferences(): AudioPreferencesV1 {
    return { ...this.prefs }
  }

  get isUnlocked(): boolean {
    return this.unlocked
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private notify(): void {
    for (const l of this.listeners) l()
  }

  setMuted(muted: boolean): void {
    this.prefs = { ...this.prefs, muted }
    this.applyGains()
    saveAudioPreferences(this.prefs)
    this.notify()
  }

  setMasterVolume(v: number): void {
    this.prefs = { ...this.prefs, masterVolume: clampVolume(v) }
    this.applyGains()
    saveAudioPreferences(this.prefs)
    this.notify()
  }

  setSfxVolume(v: number): void {
    this.prefs = { ...this.prefs, sfxVolume: clampVolume(v) }
    this.applyGains()
    saveAudioPreferences(this.prefs)
    this.notify()
  }

  setUiVolume(v: number): void {
    this.prefs = { ...this.prefs, uiVolume: clampVolume(v) }
    this.applyGains()
    saveAudioPreferences(this.prefs)
    this.notify()
  }

  setSimulationSpeed(speed: number): void {
    this.simulationSpeed = Math.max(1, speed || 1)
  }

  /** Call from a user gesture (pointer/click). Safe to call repeatedly. */
  unlock(): void {
    try {
      if (!this.ctx) {
        const AC =
          typeof window !== 'undefined'
            ? window.AudioContext ||
              (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
            : undefined
        if (!AC) return
        this.ctx = new AC()
        this.masterGain = this.ctx.createGain()
        this.sfxGain = this.ctx.createGain()
        this.uiGain = this.ctx.createGain()
        this.sfxGain.connect(this.masterGain)
        this.uiGain.connect(this.masterGain)
        this.masterGain.connect(this.ctx.destination)
        this.applyGains()
      }
      if (this.ctx.state === 'suspended') {
        void this.ctx.resume().catch(() => {
          /* autoplay policy — wait for next gesture */
        })
      }
      this.unlocked = this.ctx.state === 'running' || this.ctx.state === 'suspended'
    } catch {
      // Audio unavailable — gameplay continues silently.
    }
  }

  play(id: SoundId, options?: { force?: boolean }): boolean {
    if (this.prefs.muted && !options?.force) return false
    if (!this.ctx || !this.masterGain) {
      this.unlock()
      if (!this.ctx || !this.masterGain) return false
    }

    const nowMs = performance.now()
    const hidden = typeof document !== 'undefined' && document.hidden
    const decision = admitSound(
      id,
      {
        nowMs,
        activeVoices: this.voices.length,
        maxGlobalVoices: globalVoiceCap(this.simulationSpeed),
        lastPlayedAt: this.lastPlayedAt,
        simulationSpeed: this.simulationSpeed,
        documentHidden: hidden
      },
      options
    )
    if (!decision.admit) return false

    const policy = SOUND_POLICY[id]
    if (
      this.voices.length >= globalVoiceCap(this.simulationSpeed) &&
      shouldStealFor(policy.priority)
    ) {
      this.stealLowest()
    } else if (this.voices.length >= globalVoiceCap(this.simulationSpeed)) {
      return false
    }

    try {
      if (this.ctx.state === 'suspended') {
        void this.ctx.resume()
      }
      const bus = policy.bus === 'ui' ? this.uiGain! : this.sfxGain!
      const handle = playSynth(this.ctx, id, bus)
      const voice: ActiveVoice = {
        id,
        stop: handle.stop,
        priority: policy.priority === 'critical' ? 100 : policy.priority === 'boss' ? 80 : 30
      }
      this.voices.push(voice)
      this.lastPlayedAt.set(id, nowMs)
      // Auto-release after nominal duration.
      const timer =
        typeof window !== 'undefined' && typeof window.setTimeout === 'function'
          ? window.setTimeout.bind(window)
          : setTimeout
      timer(() => this.releaseVoice(voice), 400)
      return true
    } catch {
      return false
    }
  }

  private stealLowest(): void {
    if (this.voices.length === 0) return
    let idx = 0
    for (let i = 1; i < this.voices.length; i += 1) {
      if (this.voices[i]!.priority < this.voices[idx]!.priority) idx = i
    }
    const [v] = this.voices.splice(idx, 1)
    v?.stop()
  }

  private releaseVoice(voice: ActiveVoice): void {
    const i = this.voices.indexOf(voice)
    if (i >= 0) this.voices.splice(i, 1)
  }

  private applyGains(): void {
    if (!this.masterGain || !this.sfxGain || !this.uiGain) return
    const master = this.prefs.muted ? 0 : this.prefs.masterVolume
    this.masterGain.gain.value = master
    this.sfxGain.gain.value = this.prefs.sfxVolume
    this.uiGain.gain.value = this.prefs.uiVolume
  }

  /** Map a presentation CombatFx to at most one SFX (shared pipeline with visuals). */
  handleCombatFx(fx: CombatFx, meta?: { towerDefinitionId?: string }): void {
    switch (fx.type) {
      case 'towerPulse':
        if (fx.kind === 'fire') {
          const def = meta?.towerDefinitionId
          if (fx.multiLock) this.play('sig.multiLock')
          if (def === 'frost') this.play('tower.frost')
          else if (def === 'cannon') this.play('tower.cannon')
          else if (def === 'poison') this.play('tower.poison')
          else if (def === 'aa') this.play('tower.aa')
          else if (def === 'mage') this.play('tower.mage')
          else this.play('tower.fire')
        } else if (fx.kind === 'upgrade') this.play('ui.upgrade')
        else if (fx.kind === 'sell') this.play('ui.sell')
        break
      case 'splash':
        if (fx.innerRadius !== undefined) this.play('sig.shockwave')
        break
      case 'deepFreezeFlash':
        this.play('sig.deepFreeze')
        break
      case 'burnApply':
        this.play('sig.burn')
        break
      case 'armorBreakApply':
        this.play('sig.armorBreak')
        break
      case 'statusReject':
        this.play('enemy.statusReject')
        break
      case 'contagion':
        this.play('sig.contagion')
        break
      case 'shieldBreak':
        this.play('enemy.shieldBreak')
        break
      case 'leak':
        this.play('enemy.leak')
        break
      case 'waveClear':
        this.play('match.waveClear')
        break
      case 'death':
        // Boss death detected via color/meta elsewhere; generic deaths stay quiet.
        break
      default:
        break
    }
  }

  /** Track match/enemy presentation state for boss + victory/defeat cues. */
  syncMatchPresentation(input: {
    state: string
    enemies: Array<{ id: string; definitionId: string }>
  }): void {
    const liveBoss = new Set(
      input.enemies.filter((e) => e.definitionId === 'boss').map((e) => e.id)
    )
    for (const id of liveBoss) {
      if (!this.seenBossIds.has(id)) {
        this.play('enemy.bossSpawn')
      }
    }
    for (const id of this.seenBossIds) {
      if (!liveBoss.has(id)) {
        this.play('enemy.bossDeath')
      }
    }
    this.seenBossIds = liveBoss

    if (input.state !== this.lastMatchState) {
      if (input.state === 'VICTORY') this.play('match.victory')
      if (input.state === 'DEFEAT') this.play('match.defeat')
      this.lastMatchState = input.state
    }
  }

  resetMatchBookkeeping(): void {
    this.seenBossIds.clear()
    this.lastMatchState = null
    this.lastPlayedAt.clear()
    for (const v of this.voices) v.stop()
    this.voices = []
  }

  dispose(): void {
    this.resetMatchBookkeeping()
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }
    void this.ctx?.close().catch(() => undefined)
    this.ctx = null
  }

  /** Test/diagnostics */
  getDebugCount(): number {
    return this.voices.length
  }
}

let shared: AudioManager | null = null

export function getAudioManager(): AudioManager {
  if (!shared) shared = new AudioManager()
  return shared
}

export function resetAudioManagerForTests(prefs?: AudioPreferencesV1): AudioManager {
  shared?.dispose()
  shared = new AudioManager(prefs)
  return shared
}
